// src/pages/receptionist-workspace/index.tsx
//
// One route element for every Receptionist screen, mirroring
// @page/oura-workspace. Screens that are still to be built render a placeholder
// so the rail is navigable end to end while they land.

import { Navigate, useNavigate, useParams } from "react-router-dom";
import ReceptionistShell from "@container/layout/receptionist-shell";
import { RECEPTIONIST_ROOT, receptionistHref, type View } from "@receptionist/nav";
import { useReceptionistRole, useReceptionistMerchantId } from "@receptionist/role";
import { MerchantsScreen } from "@receptionist/screens/Merchants";
import { SettingsScreen } from "@receptionist/screens/Settings";
import { MerchantGate } from "@receptionist/merchantState";
import { FlowGate } from "@receptionist/flowState";
import { ConversationScreen } from "@receptionist/screens/Conversation";
import { BookingsScreen } from "@receptionist/screens/Bookings";
import { ServicesScreen } from "@receptionist/screens/Services";
import { BehaviourScreen } from "@receptionist/screens/Behaviour";
import { StaffScreen } from "@receptionist/screens/Staff";
import { BusinessProfileScreen } from "@receptionist/screens/BusinessProfile";
import { LearnFromChatsScreen } from "@receptionist/screens/LearnFromChats";
import { SetupBanner } from "@receptionist/components/SetupBanner";
import { SetupScreen, type SetupStepKey } from "@receptionist/screens/Setup";
import { OverviewScreen } from "@receptionist/screens/Overview";

/** /receptionist → the signed-in role's home. */
export function ReceptionistHomeRedirect() {
    const role = useReceptionistRole();
    return (
        <Navigate
            to={role === "receptionist-staff" ? `${RECEPTIONIST_ROOT}/merchants` : `${RECEPTIONIST_ROOT}/home`}
            replace
        />
    );
}

export default function ReceptionistWorkspace({ screen }: { screen: View }) {
    const role = useReceptionistRole();
    const navigate = useNavigate();
    const params = useParams<{ merchantId?: string }>();
    const ownMerchantId = useReceptionistMerchantId();
    /**
     * Which merchant this console is showing. Staff drill in from the roll-up,
     * so for them it is the URL's `:merchantId` (ourteam-frontend#3611); a
     * client has exactly one, from the JWT (#3612). Screens receive it as
     * their tickets wire them (#3607 onward); until then the fixtures ignore
     * it. Keyed on the layout's children so every screen's local state resets
     * when staff switch merchant — a form half-edited for one merchant must
     * not survive into the next.
     */
    const merchantId = (role === "receptionist-staff" ? params.merchantId : null) ?? ownMerchantId;
    const setupHref = (step?: SetupStepKey) => `${receptionistHref("setup", { role, merchantId })}${step ? `?step=${step}` : ""}`;
    // Business, Staff and Learn are steps of Setup now (#4796); their old
    // addresses open the step, so a bookmark or an old link still lands.
    const asStep: Partial<Record<View, SetupStepKey>> = { profile: "business", staff: "team", learn: "learn" };
    const step = asStep[screen];
    if (step) return <Navigate to={setupHref(step)} replace />;
    return (
        <ReceptionistShell>
            <div key={merchantId ?? "own"}>
            {/* Every merchant screen, not the staff roll-up or Settings (#4789).
                `merchantId` is already the login's own merchant for a client
                (`?? ownMerchantId` above); null only when there is none. */}
            {/* Not on Overview either: it carries its own Finish setup card (#4847). */}
            {screen !== "overview" && screen !== "settings" && screen !== "setup" && screen !== "home" && merchantId && (
                <SetupBanner merchantId={merchantId} setupHref={setupHref()} onSetupPage={false} />
            )}
            {screen === "setup" && (
                <SetupScreen
                    merchantId={merchantId}
                    servicesHref={receptionistHref("knowledge", { role, merchantId })}
                    followUpsHref={`${receptionistHref("flow", { role, merchantId })}?tab=followups`}
                />
            )}
            {/* The flow builder explains itself with the merchant's own bookable
                services, read from the same loaded config the Services screen
                edits, so the two can never disagree. */}
            {screen === "flow" && (
                <MerchantGate merchantId={merchantId}>
                    {({ merchant }) => (
                        <FlowGate merchantId={merchantId}>
                            {(flowProps) => (
                                <ConversationScreen
                                    {...flowProps}
                                    services={merchant.services.filter((s) => s.bookable).map((s) => s.name)}
                                    merchantId={merchantId}
                                    templatesHref={setupHref("templates")}
                                />
                            )}
                        </FlowGate>
                    )}
                </MerchantGate>
            )}
            {screen === "home" && (
                <OverviewScreen
                    merchantId={merchantId}
                    hrefs={{
                        bookings: receptionistHref("bookings", { role, merchantId }),
                        booking: (id) => `${receptionistHref("bookings", { role, merchantId })}?open=${encodeURIComponent(id)}`,
                        setup: (s) => setupHref(s),
                        followUps: `${receptionistHref("flow", { role, merchantId })}?tab=followups`,
                    }}
                />
            )}
            {screen === "bookings" && (
                <BookingsScreen
                    merchantId={merchantId}
                    profileHref={setupHref("templates")}
                    backHref={role === "receptionist-staff" ? `${RECEPTIONIST_ROOT}/merchants` : undefined}
                />
            )}
            {/* The four views onto MerchantConfig share one loader (#3607). */}
            {(screen === "knowledge" || screen === "assistant" || screen === "staff" || screen === "profile") && (
                <MerchantGate merchantId={merchantId}>
                    {(props) => (
                        <>
                            {screen === "knowledge" && <ServicesScreen {...props} />}
                            {screen === "assistant" && <BehaviourScreen {...props} />}
                            {screen === "staff" && <StaffScreen {...props} />}
                            {screen === "profile" && <BusinessProfileScreen {...props} />}
                        </>
                    )}
                </MerchantGate>
            )}
            {screen === "learn" && <LearnFromChatsScreen merchantId={merchantId} />}
            {screen === "settings" && <SettingsScreen role={role} />}
            {screen === "overview" && (
                <MerchantsScreen
                    onSelectMerchant={(id) =>
                        navigate(receptionistHref("home", { role: "receptionist-staff", merchantId: id }))
                    }
                />
            )}
            </div>
        </ReceptionistShell>
    );
}
